import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { GOOGLE_CLIENT_ID } from "../config";
import {
  getToken,
  getUser,
  refreshSession,
  signOut as revokeSession,
  subscribeToLogout,
  type AuthUser,
} from "../auth";
import { ApiError } from "../api";
import { api } from "../queries";
import Login from "../pages/Login";

const SESSION_MESSAGES: Record<string, string> = {
  "session-expired": "Your session expired — please sign in again.",
  "signed-out-elsewhere": "You were signed out in another tab.",
};

type AuthState = {
  user: AuthUser | null;
  ready: boolean;
  authRequired: boolean;
};

const AuthContext = createContext<{
  user: AuthUser | null;
  authRequired: boolean;
  signOut: () => void;
} | null>(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export default function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ user: null, ready: false, authRequired: true });
  const [sessionReason, setSessionReason] = useState<string | null>(null);

  useEffect(() => subscribeToLogout((reason) => {
    setState({ user: null, ready: true, authRequired: true });
    setSessionReason(reason);
  }), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // The access token lives in memory, so a reload always starts with none —
      // but the httpOnly refresh cookie may still be good for weeks. Renew
      // silently BEFORE probing /auth/check: otherwise a perfectly live session
      // looks unauthenticated here and bounces the user back to Google.
      await refreshSession();
      if (cancelled) return;
      try {
        const check = await api.authCheck();
        if (cancelled) return;
        const token = getToken();
        if (token) {
          const stored = getUser();
          setState({
            user: stored ?? { user_id: check.user_id, email: check.email },
            ready: true,
            authRequired: true,
          });
          return;
        }
        setState({
          user: { user_id: check.user_id, email: check.email ?? "local-dev" },
          ready: true,
          authRequired: false,
        });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          setState({ user: getToken() ? getUser() : null, ready: true, authRequired: true });
          return;
        }
        setState({ user: null, ready: true, authRequired: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(
    () => ({
      user: state.user,
      authRequired: state.authRequired,
      signOut: () => {
        void revokeSession();
      },
    }),
    [state.user, state.authRequired],
  );

  if (!state.ready) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <span className="loading loading-spinner loading-lg" />
      </div>
    );
  }

  if (state.authRequired && !state.user) {
    return (
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <Login
          message={sessionReason ? SESSION_MESSAGES[sessionReason] : undefined}
          onLogin={(u) => {
            setSessionReason(null);
            setState({ user: u, ready: true, authRequired: true });
          }}
        />
      </GoogleOAuthProvider>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
